import React, { useRef, useState } from "react";
import uploadIcon from "../../assets/images/upload.svg";
import "react-toastify/dist/ReactToastify.css";
import { toast } from "react-toastify";
import StorageHelper from "../../Utility/StorageHelper";
import { FnAuthenticatedFileUpload, SERVICES } from "../../Utility/Utils";
import { useHistory } from "react-router-dom";
import ExcelJS from "exceljs";

// Server request body limit is 30 MB by default
const MAX_FILE_SIZE_MB = 25;
const MAX_ERRORS_ON_SCREEN = 100;
// Data rows only, header row not counted
const MAX_DATA_ROWS = 2000;

interface UploadError {
  rowNo: number;
  error: string;
}

interface UploadResult {
  fileName: string;
  totalRows: number;
  insertedRows: number;
  updatedRows: number;
  skippedRows: number;
  duplicateRows: number;
  ignoredHeaders: string[];
  errors: UploadError[];
}

// Counts non-empty rows below the header (first non-empty row)
const countDataRows = async (uploadedFile: File) => {
  const workbook = new ExcelJS.Workbook();
  await workbook.xlsx.load(await uploadedFile.arrayBuffer());
  const sheet = workbook.worksheets[0];
  if (!sheet) return 0;

  let headerFound = false;
  let dataRows = 0;
  sheet.eachRow((row) => {
    if (row.actualCellCount === 0) return;
    if (!headerFound) {
      headerFound = true;
      return;
    }
    dataRows++;
  });
  return dataRows;
};

const downloadErrors = async (uploadedFile: File, errors: UploadError[]) => {
  const sourceWorkbook = new ExcelJS.Workbook();
  await sourceWorkbook.xlsx.load(await uploadedFile.arrayBuffer());
  const sourceSheet = sourceWorkbook.worksheets[0];

  let headerRowNo = 1;
  for (let r = 1; r <= sourceSheet.rowCount; r++) {
    if (sourceSheet.getRow(r).actualCellCount > 0) {
      headerRowNo = r;
      break;
    }
  }
  const headerRow = sourceSheet.getRow(headerRowNo);
  const columnCount = sourceSheet.columnCount;
  const errorColumn = columnCount + 1;

  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet(sourceSheet.name || "Sheet1");

  const newHeader = worksheet.getRow(1);
  for (let c = 1; c <= columnCount; c++) {
    newHeader.getCell(c).value = headerRow.getCell(c).value;
  }
  newHeader.getCell(errorColumn).value = "error";
  newHeader.font = { bold: true };

  [...errors]
    .sort((a, b) => a.rowNo - b.rowNo)
    .forEach((rowError, index) => {
      const sourceRow = sourceSheet.getRow(rowError.rowNo);
      const newRow = worksheet.getRow(index + 2);
      // Copy raw values so numbers stay numbers and dates stay dates
      for (let c = 1; c <= columnCount; c++) {
        newRow.getCell(c).value = sourceRow.getCell(c).value;
      }
      newRow.getCell(errorColumn).value = rowError.error;
      newRow.getCell(errorColumn).font = { color: { argb: "FFFF0000" } };
    });

  worksheet.getColumn(errorColumn).width = 45;

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = `${uploadedFile.name.replace(/\.xlsx$/i, "")}_errors.xlsx`;
  link.click();
  URL.revokeObjectURL(link.href);
};

export default function LongSurveyUpload() {
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadResult, setUploadResult] = useState<UploadResult | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const history = useHistory();

  const handleRemoveFile = () => {
    setFile(null);
    setUploadProgress(0);
    setUploadResult(null);
    // Reset file input value
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (!selectedFile) return;

    if (!selectedFile.name.toLowerCase().endsWith(".xlsx")) {
      handleRemoveFile();
      toast.error("Invalid file. Please upload a .xlsx file", {
        position: toast.POSITION.TOP_CENTER,
        autoClose: 3000,
      });
      return;
    }

    if (selectedFile.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      handleRemoveFile();
      toast.error(`File size should not exceed ${MAX_FILE_SIZE_MB} MB`, {
        position: toast.POSITION.TOP_CENTER,
        autoClose: 3000,
      });
      return;
    }

    let dataRows = 0;
    try {
      dataRows = await countDataRows(selectedFile);
    } catch (error) {
      console.error(error);
      handleRemoveFile();
      toast.error("Unable to read file. Please upload a valid .xlsx file", {
        position: toast.POSITION.TOP_CENTER,
        autoClose: 3000,
      });
      return;
    }

    if (dataRows > MAX_DATA_ROWS) {
      handleRemoveFile();
      toast.error(
        `File has ${dataRows} rows. Maximum ${MAX_DATA_ROWS} rows allowed per upload`,
        {
          position: toast.POSITION.TOP_CENTER,
          autoClose: 3000,
        }
      );
      return;
    }

    setFile(selectedFile);
    setUploadResult(null);
  };

  const handleSave = async () => {
    if (!file) return;

    try {
      setUploading(true);
      setUploadProgress(0);

      const authData = JSON.parse(StorageHelper.get("auth") || "{}");
      const authHeader = {
        JWTToken: authData.jwtToken,
        userId: authData.userId,
      };

      const response: any = await FnAuthenticatedFileUpload(
        "LongSurvey/Upload",
        authHeader,
        file,
        SERVICES.FKBO,
        (progressEvent: any) => {
          if (progressEvent?.total) {
            setUploadProgress(
              Math.round((progressEvent.loaded * 100) / progressEvent.total)
            );
          }
        }
      );

      // data comes as an object even when no row was valid, so errors can be shown
      if (response?.data && typeof response.data === "object") {
        setUploadResult(response.data);
      }

      const { insertedRows = 0, updatedRows = 0 } = response?.data || {};

      if (response?.success && insertedRows + updatedRows === 0) {
        // Request worked but every row was skipped - nothing saved
        toast.error("No rows saved. Please fix the errors below and upload again.", {
          position: toast.POSITION.TOP_CENTER,
          autoClose: 3000,
        });
      } else if (response?.success) {
        toast.success(`${insertedRows} inserted, ${updatedRows} updated`, {
          position: toast.POSITION.TOP_CENTER,
          style: { backgroundColor: "#258750", color: "white" },
          autoClose: 3000,
        });
      } else {
        toast.error(response?.error || "Something went wrong", {
          position: toast.POSITION.TOP_CENTER,
          autoClose: 3000,
        });
      }
    } catch (error: any) {
      console.error(error);
      toast.error(
        error?.response?.data?.error || error.message || "Something went wrong",
        {
          position: toast.POSITION.TOP_CENTER,
          autoClose: 3000,
        }
      );
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="p-5 pb-32">
      <div className="block sm:flex items-center">
        <h1 className="text-xl font-bold px-3 py-3">Long Survey Upload</h1>
      </div>
      <hr className="mt-4 border border-gray-200" />

      <div className="mt-10">
        <div className="flex justify-center mt-0">
          <div
            className={`border-2 border-dashed border-opacity-50 border-gray-400 ${file ? `bg-green-300` : `bg-white`
              } flex flex-col justify-center items-center px-28 py-12`}
          >
            <input
              type="file"
              ref={fileInputRef}
              id="longSurveyFileInput"
              accept=".xlsx"
              className="hidden"
              onChange={handleFileSelect}
            />
            <img
              src={uploadIcon}
              alt="Long Survey Upload Img"
              className="cursor-pointer"
              onClick={() => !uploading && fileInputRef.current?.click()}
            />
            {file && (
              <div>
                <span className="text-wrap w-2">
                  {file.name} ({(file.size / (1024 * 1024)).toFixed(2)} MB)
                </span>
                {!uploading && (
                  <span
                    className="cursor-pointer text-2xl text-red-500 ml-2"
                    onClick={handleRemoveFile}
                  >
                    x
                  </span>
                )}
              </div>
            )}

            {uploading ? (
              <p className="text-[#009CA7] font-semibold mt-4 text-base">
                {uploadProgress < 100
                  ? `Uploading... ${uploadProgress}%`
                  : "Processing on server..."}
              </p>
            ) : (
              <p
                className="text-[#009CA7] cursor-pointer font-semibold mt-4 text-base"
                onClick={() => {
                  handleRemoveFile();
                  fileInputRef.current?.click();
                }}
              >
                {file ? "Replace File" : "Upload File"}
              </p>
            )}
          </div>
        </div>
      </div>
      <div className="flex justify-center items-center mt-7">
        <h1 className="text-base">
          Use xlsx format ({MAX_DATA_ROWS} rows), or{" "}
          <a
            href="/long_survey_template.xlsx"
            className="text-[#009CA7] font-bold"
            download
          >
            Download Template
          </a>
        </h1>
      </div>

      {uploadResult && (
        <div className="mt-8 mx-5">
          <h1 className="text-sm">
            <span className="font-bold">{uploadResult.fileName}</span>:{" "}
            {uploadResult.totalRows} rows |{" "}
            <span className="text-[#009CA7] font-bold">
              {uploadResult.insertedRows} inserted
            </span>{" "}
            |{" "}
            <span className="text-[#009CA7] font-bold">
              {uploadResult.updatedRows} updated
            </span>{" "}
            |{" "}
            <span className="text-[#FF0000] font-bold">
              {uploadResult.skippedRows} skipped
            </span>
            {uploadResult.duplicateRows > 0 &&
              ` | ${uploadResult.duplicateRows} duplicate rows (last one used)`}
          </h1>
          {uploadResult.ignoredHeaders?.length > 0 && (
            <h1 className="text-sm mt-2">
              Ignored columns: {uploadResult.ignoredHeaders.join(", ")}
            </h1>
          )}
          {uploadResult.errors?.length > 0 && (
            <>
              <h1 className="text-sm mt-2">
                Showing first{" "}
                {Math.min(uploadResult.errors.length, MAX_ERRORS_ON_SCREEN)}{" "}
                errors.
                <span
                  className="text-[#009CA7] px-1 cursor-pointer underline"
                  onClick={async () => {
                    if (!file) return;
                    try {
                      await downloadErrors(file, uploadResult.errors);
                    } catch (error) {
                      console.error(error);
                      toast.error("Unable to download error file", {
                        position: toast.POSITION.TOP_CENTER,
                        autoClose: 3000,
                      });
                    }
                  }}
                >
                  Download all
                </span>
              </h1>
              <div className="mt-3 max-h-[300px] overflow-auto border-2 border-gray-300">
                <table className="min-w-full text-sm text-left">
                  <thead className="bg-[#E5F5F6] sticky top-0">
                    <tr>
                      <th className="px-4 py-2 font-bold">Excel Row No</th>
                      <th className="px-4 py-2 font-bold">Error</th>
                    </tr>
                  </thead>
                  <tbody>
                    {uploadResult.errors
                      .slice(0, MAX_ERRORS_ON_SCREEN)
                      .map((rowError) => (
                        <tr key={rowError.rowNo} className="border-t">
                          <td className="px-4 py-2">{rowError.rowNo}</td>
                          <td className="px-4 py-2">{rowError.error}</td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}

      {/* Solid background so the footer doesn't overlap the error table */}
      <div className="flex fixed bottom-0 left-16 right-0 py-4 bg-white border-t border-gray-200 justify-center items-center gap-10 sm:gap-96">
        <button
          className="flex px-6 py-1.5 border-2 border-grey-300 rounded-md bg-white mr-4"
          disabled={uploading}
          onClick={() => {
            history.push("/about");
          }}
        >
          Cancel
        </button>
        <button
          disabled={!file || uploading}
          className={`flex px-8 py-1.5 border-2 border-grey-300 rounded-md text-white ${!file || uploading ? `bg-gray-400` : `bg-[#009CA7]`
            } `}
          onClick={handleSave}
        >
          {uploading ? "Uploading..." : "Save"}
        </button>
      </div>
    </div>
  );
}
